/**
 * "Scan truth" — one unified end-of-session debug summary for a Nexus Dex
 * live-scan run (effectiveness plan item 3, 2026-06-23).
 *
 * WHY THIS EXISTS. A live scan already emits rich telemetry, but it is spread
 * across THREE independent surfaces a tester must mentally join by hand:
 *
 *   1. `scanPipelineTelemetry` (module-level)  — frame intake: received / gate
 *      passed / gate dropped / OCR attempts+hits. Answers "did frames arrive
 *      and reach OCR?".
 *   2. `dexScanTelemetry` (per-session recorder) — the OBSERVE-vs-COMMIT split:
 *      rows observed vs rows committed. Answers "was a row READ even though the
 *      strict write gates declined to APPLY it?".
 *   3. `DexLiveSessionTotals` (session)         — applied entries plus the
 *      per-gate veto tallies (invalid dex member / sprite-state / slot-map
 *      provenance cross-check / overlay text) and the two-frame corroboration
 *      counts. Answers "which gate held the row back?".
 *
 * When a device scan "feels broken", the tester has to grep three different log
 * prefixes and line them up to find the ONE stage where the funnel collapsed
 * (frames in → gate → OCR → rows observed → rows committed → entries applied).
 * This module folds all three into a single funnel so the failure point is
 * obvious at a glance, and emits it under one grep-friendly prefix.
 *
 * HONEST LABELS. The summary deliberately keeps OBSERVED/READ separate from
 * COMMITTED/APPLIED (see `rowsObserved` vs `rowsCommitted` vs `entriesApplied`)
 * because conflating them is exactly what makes a working scan look broken and
 * vice-versa. The blocked-by-gate tallies explain the gap between the two.
 *
 * PURE BOOKKEEPING. This module only READS snapshots other code already
 * produced. It never applies an entry, never relaxes a write gate, and never
 * mutates any of its inputs — so it is safe to call at any time and is unit
 * tested deterministically (see scanTruthSummary.test.ts).
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import type { DexLiveSessionTotals } from "./dexLiveScanSession";
import type { ScanTelemetrySnapshot } from "./dexScanTelemetry";
import type { ScanPipelineTelemetry } from "./scanPipelineTelemetry";
import type { LiveScanFrameStats } from "./liveScanFrameStats";

/**
 * Native App-Group frame-intake evidence for the run, when the poller recorded
 * it. Distinct from the JS `pipeline` numbers: these are what the host app
 * pulled out of the App Group container BEFORE the JS loop saw them, so a scan
 * that wrote frames to disk but never dispatched them is visible here even when
 * the JS pipeline counters are all zero.
 */
export interface ScanTruthNativeIntake {
  /** listFrames() loop ticks the poller ran. */
  loopTicks: number;
  /** Frames consumed from the App Group container (post stale-filter). */
  framesConsumed: number;
  /** Frames dispatched into the OCR pipeline via onFrame. */
  framesDispatched: number;
  /** consumeFrame() returned null (file vanished between list and consume). */
  framesConsumeFailed: number;
  /** consumeFrame() returned bytes but base64 decode produced no payload. */
  framesDecodeFailed: number;
}

/**
 * Durable dex-store write outcome for the run, when it is plumbed. `applied`
 * (committed) entries already imply a SUCCESSFUL durable write — the store's
 * `applyScanObservations` reports zero applied if `safeWriteStore` fails (the
 * PiP-trust fix), so a non-zero `entriesApplied` IS the success signal. This
 * optional field carries an explicit success/failure COUNT when a caller has
 * that signal available; it is `null` when the run did not separately
 * instrument store-write outcomes (the common case today).
 */
export interface ScanTruthStoreWrite {
  /** Durable store writes that landed. */
  ok: number;
  /** Durable store writes that were attempted but failed to persist. */
  failed: number;
}

/**
 * The unified funnel. Read top to bottom: each stage should be <= the stage
 * above it. The first place the number collapses to ~0 is where the scan broke.
 */
export interface ScanTruthSummary {
  // ── Acquire: did frames arrive and reach OCR? ──────────────────────────────
  /** listFrames() loop ticks (native intake), or null when not recorded. */
  loopTicks: number | null;
  /** Frames the JS loop saw (received + suppressed byte-identical duplicates). */
  framesSeen: number;
  /** Frames the session ingested (ran the per-frame apply pipeline on). */
  framesIngested: number;
  /** Frames the pre-OCR gate allowed through to OCR. */
  framesAllowedByGate: number;
  /** Frames the pre-OCR gate dropped before OCR. */
  framesDroppedByGate: number;
  /** OCR fanout invocations. */
  framesOcrd: number;
  /** OCR invocations that yielded >= 1 dex number. */
  ocrHits: number;
  /** Cumulative OCR cell results across ingested frames. */
  cellOcrResults: number;
  /** Cumulative dex numbers parsed across ingested frames. */
  numbersRead: number;

  // ── Observe vs commit: read in context, but did it apply? ──────────────────
  /** Distinct rows READ in the correct context (may never have applied). */
  rowsObserved: number;
  /** Subset of rowsObserved that committed >= 1 entry. */
  rowsCommitted: number;
  /** rowsObserved that never committed — the load-bearing "read but blocked". */
  rowsObservedNotCommitted: number;
  /** Distinct (variant, dexNumber) entries APPLIED to the store this run. */
  entriesApplied: number;

  // ── Why a read row did not commit (per-gate veto tallies) ──────────────────
  /** Σ present writes blocked by the curated-roster membership veto. */
  blockedInvalidDexMember: number;
  /** Σ present writes blocked by the sprite-state veto (G-Max weak-sprite). */
  blockedSpriteStateVeto: number;
  /**
   * Σ cells dropped by the region-provenance cross-check (the dexId-vs-region
   * guard — the aggregated slot-map/provenance gate). The per-cell slot-map
   * present-write downgrade (`[dex-scan] slot-map present-write blocked`) is not
   * yet aggregated into its own counter; it remains visible via that log line.
   */
  blockedSlotMapProvenance: number;
  /** Σ present writes blocked by the Nexus overlay-text veto. */
  blockedOverlayText: number;
  /** Σ (slot,id) sightings held awaiting two-frame corroboration. */
  twoFramePending: number;
  /** Σ (slot,id) sightings that cleared two-frame corroboration. */
  twoFrameConfirmed: number;

  // ── Context + write outcome ────────────────────────────────────────────────
  /** Native App-Group intake evidence, or null when not recorded. */
  nativeIntake: ScanTruthNativeIntake | null;
  /** Durable store-write success/failure counts, or null when not instrumented. */
  storeWrite: ScanTruthStoreWrite | null;
  /** Most recent frame's resolved variant, when known. */
  lastVariant: PokedexVariant | null;
  /** Most recent frame's region label, when known. */
  lastRegion: string;
}

export interface ScanTruthInputs {
  /** Per-session totals from `createDexLiveScanSession().snapshot()`. */
  totals: DexLiveSessionTotals;
  /** Observe/commit recorder snapshot from `telemetrySnapshot()`. */
  telemetry: ScanTelemetrySnapshot;
  /** Frame-intake counters from `readScanPipelineTelemetry()`. */
  pipeline: ScanPipelineTelemetry;
  /** Native App-Group poller stats, when available (else null/undefined). */
  frameStats?: LiveScanFrameStats | null;
  /** Explicit durable store-write counts, when a caller has them (else null). */
  storeWrite?: ScanTruthStoreWrite | null;
}

/**
 * Fold the three telemetry surfaces (+ optional native intake / store-write
 * signals) into one funnel. Pure: never mutates its inputs.
 */
export function buildScanTruthSummary(inputs: ScanTruthInputs): ScanTruthSummary {
  const { totals, telemetry, pipeline } = inputs;
  const fs = inputs.frameStats ?? null;

  const nativeIntake: ScanTruthNativeIntake | null = fs
    ? {
        loopTicks: fs.ticks,
        framesConsumed: fs.framesConsumed,
        framesDispatched: fs.framesDispatched,
        framesConsumeFailed: fs.framesConsumeFailed,
        framesDecodeFailed: fs.framesDecodeFailed,
      }
    : null;

  return {
    loopTicks: nativeIntake ? nativeIntake.loopTicks : null,
    framesSeen: pipeline.framesReceived + pipeline.framesSkippedDuplicate,
    framesIngested: totals.framesObserved,
    framesAllowedByGate: pipeline.framesPassedGate,
    framesDroppedByGate: pipeline.framesDroppedGate,
    framesOcrd: pipeline.ocrAttempts,
    ocrHits: pipeline.ocrHits,
    cellOcrResults: totals.cellsObserved,
    numbersRead: totals.numbersRead,

    rowsObserved: telemetry.rowsObserved,
    rowsCommitted: telemetry.rowsCommitted,
    rowsObservedNotCommitted: telemetry.rowsObservedNotCommitted,
    entriesApplied: totals.applied,

    blockedInvalidDexMember: totals.gmaxQa.invalidDexMember,
    blockedSpriteStateVeto: totals.gmaxQa.gmaxWeakSprite,
    blockedSlotMapProvenance: totals.gated.cross_check_drop,
    blockedOverlayText: totals.gmaxQa.overlayTextBlock,
    twoFramePending: totals.gmaxQa.twoFramePending,
    twoFrameConfirmed: totals.gmaxQa.twoFrameConfirmed,

    nativeIntake,
    storeWrite: inputs.storeWrite ?? null,
    lastVariant: totals.lastVariant,
    lastRegion: totals.lastRegion,
  };
}

/**
 * Compact one-line triage of the acquire funnel, for an at-a-glance read on a
 * phone. The arrow chain is the path: the first stage that collapses to ~0 is
 * the stall (frames in -> ingested -> gate -> ocr -> rows obs -> committed ->
 * applied).
 */
export function formatScanTruthLine(s: ScanTruthSummary): string {
  const ticks = s.loopTicks != null ? `ticks ${s.loopTicks} -> ` : "";
  return (
    `${ticks}seen ${s.framesSeen} -> ingest ${s.framesIngested}` +
    ` -> gate ${s.framesAllowedByGate} (drop ${s.framesDroppedByGate})` +
    ` -> ocr ${s.framesOcrd} hits ${s.ocrHits}` +
    ` -> rows obs ${s.rowsObserved} commit ${s.rowsCommitted}` +
    ` -> applied ${s.entriesApplied}`
  );
}

const SCAN_TRUTH_LOG_PREFIX = "[dex-scan] scan-truth";

/**
 * Emit the whole-session scan-truth summary to the structured log in a handful
 * of grep-friendly lines so a tester can answer "where did this scan break?"
 * from an Xcode/TestFlight trace WITHOUT a debug build:
 *
 *   [dex-scan] scan-truth funnel  — the one-line acquire funnel
 *   [dex-scan] scan-truth flow    — frame intake + OCR counters
 *   [dex-scan] scan-truth observe-vs-commit — rows observed vs committed vs applied
 *   [dex-scan] scan-truth blocked — only the non-zero per-gate veto tallies
 *   [dex-scan] scan-truth context — variant/region + native intake + store write
 *
 * `log` defaults to `console.log` so it lands in the device trace alongside the
 * existing telemetry markers; pass a capturing fn in tests. Pure read — calling
 * it twice is safe.
 */
export function logScanTruthSummary(
  inputs: ScanTruthInputs,
  log: (event: string, fields: Record<string, unknown>) => void = (
    event,
    fields,
  ) => {
    // eslint-disable-next-line no-console
    console.log(`${SCAN_TRUTH_LOG_PREFIX} ${event}`, fields);
  },
): ScanTruthSummary {
  const s = buildScanTruthSummary(inputs);

  log("funnel", { line: formatScanTruthLine(s) });
  log("flow", {
    loopTicks: s.loopTicks,
    framesSeen: s.framesSeen,
    framesIngested: s.framesIngested,
    framesAllowedByGate: s.framesAllowedByGate,
    framesDroppedByGate: s.framesDroppedByGate,
    framesOcrd: s.framesOcrd,
    ocrHits: s.ocrHits,
    cellOcrResults: s.cellOcrResults,
    numbersRead: s.numbersRead,
  });
  log("observe-vs-commit", {
    rowsObserved: s.rowsObserved,
    rowsCommitted: s.rowsCommitted,
    rowsObservedNotCommitted: s.rowsObservedNotCommitted,
    entriesApplied: s.entriesApplied,
    twoFramePending: s.twoFramePending,
    twoFrameConfirmed: s.twoFrameConfirmed,
  });
  // Only surface gates that actually fired so the line stays readable.
  const blocked: Record<string, number> = {};
  if (s.blockedInvalidDexMember > 0) blocked.invalidDexMember = s.blockedInvalidDexMember;
  if (s.blockedSpriteStateVeto > 0) blocked.spriteStateVeto = s.blockedSpriteStateVeto;
  if (s.blockedSlotMapProvenance > 0) blocked.slotMapProvenance = s.blockedSlotMapProvenance;
  if (s.blockedOverlayText > 0) blocked.overlayText = s.blockedOverlayText;
  log("blocked", blocked);
  log("context", {
    lastVariant: s.lastVariant,
    lastRegion: s.lastRegion,
    nativeIntake: s.nativeIntake,
    storeWrite: s.storeWrite,
  });

  return s;
}
